import { Link } from "react-router-dom";
import { ArrowRight, ScanEye, Sparkles } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { Reveal } from "../components/motion";
import { Specialties, OtherServices } from "../components/sections";
import { RETINA_CATEGORIES, IOL_TYPES } from "../lib/constants";

const Services = () => (
  <>
    <PageHero
      kicker="Services"
      testid="services-hero"
      lines={[
        <>Every service,</>,
        <>
          <span className="font-serif italic font-semibold text-brand-bright">specialist</span> led.
        </>,
      ]}
      sub="From routine eye examinations to complex vitreoretinal surgery — comprehensive ophthalmic care at Janak Puri, New Delhi."
    />

    <section className="py-20 sm:py-28" data-testid="services-specialties">
      <div className="container-x">
        <SectionHead
          chapter="01"
          kicker="Primary Specialties"
          title="Retina & cataract, first"
          sub="Our core specialisations — backed by focused training and high surgical volumes."
        />
        <div className="mt-12">
          <Specialties />
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 bg-slate-50" data-testid="services-retina-overview">
      <div className="container-x grid gap-10 lg:grid-cols-[0.8fr_1.2fr] items-start">
        <div className="lg:sticky lg:top-28">
          <SectionHead
            chapter="02"
            kicker="Retina Care"
            title="Advanced retina care"
            sub="Vitreo-retinal surgery, intravitreal injections and retina laser — under one roof."
          />
          <Reveal delay={0.15} className="mt-8">
            <Link to="/retina" className="btn-primary" data-testid="services-retina-button">
              Retina Services in Detail <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <div className="space-y-4">
          {RETINA_CATEGORIES.map((cat, i) => (
            <Reveal key={cat.title} delay={i * 0.08}>
              <div className="card-lift rounded-2xl border border-slate-200 bg-white p-6 sm:p-7">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-brand-mist flex items-center justify-center">
                    <ScanEye className="w-5 h-5 text-brand-bright" strokeWidth={1.7} aria-hidden="true" />
                  </span>
                  <h3 className="text-lg font-extrabold text-ink">{cat.title}</h3>
                </div>
                <p className="mt-3 text-sm text-slate-600">{cat.blurb}</p>
                <p className="mt-3 text-xs font-bold text-aqua uppercase tracking-wider">{cat.items.length} procedures & therapies</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28" data-testid="services-cataract-overview">
      <div className="container-x grid gap-10 lg:grid-cols-[0.8fr_1.2fr] items-start">
        <div className="lg:sticky lg:top-28">
          <SectionHead
            chapter="03"
            kicker="Cataract Care"
            title="Advanced cataract surgery"
            sub="Micro-incisional sutureless surgery with premium intra-ocular lenses."
          />
          <Reveal delay={0.15} className="mt-8">
            <Link to="/cataract" className="btn-primary" data-testid="services-cataract-button">
              Cataract Surgery in Detail <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <Reveal>
            <div className="rounded-2xl bg-ink text-white noise-dark relative overflow-hidden p-7 sm:col-span-2">
              <Sparkles className="relative w-6 h-6 text-aqua-bright" strokeWidth={1.6} aria-hidden="true" />
              <h3 className="relative mt-4 text-xl font-extrabold">Micro-Incisional Sutureless Cataract Surgery (MICS)</h3>
              <p className="relative mt-2 text-sm text-blue-100/80 leading-relaxed">
                Stitch-free, small-incision cataract surgery with premium IOL options tailored to your eyes.
              </p>
            </div>
          </Reveal>
          {IOL_TYPES.map((iol, i) => (
            <Reveal key={iol.name} delay={0.08 * (i + 1)}>
              <div className="card-lift h-full rounded-2xl border border-slate-200 bg-white p-6">
                <h4 className="text-base font-extrabold text-ink">{iol.name}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{iol.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 bg-slate-50" data-testid="services-other">
      <div className="container-x">
        <SectionHead
          chapter="04"
          kicker="Complete Eye Care"
          title="Complete eye care under one roof"
          sub="Comprehensive ophthalmic evaluation and care for the whole family."
        />
        <div className="mt-12">
          <OtherServices />
        </div>
      </div>
    </section>
  </>
);

export default Services;
